import React, { useEffect, useState, useRef } from 'react'
import { connect, useDispatch } from 'react-redux'
import { RootState } from 'Store'
import { Alert, Col, Dropdown, Menu, Row, Space } from 'antd'
import { formatAddress, formatDateTime, numberWithComma } from 'helpers/format'
import { selectCurrentUser } from 'Store/user/selector'
import EventModal from 'Components/modal/EventModal'
import { clearEventAction } from 'Store/grn-payment/actions'
import LoadingView from 'Components/modal/LoadingView'
import { DetailDrawer } from 'Components/common/MainLayout'
import styled from 'styled-components'
import { MyTheme } from 'Components/common/Theme'
import { selectAllSupplierList } from 'Store/supplier/selector'
import { InfoItem } from 'Pages/Merchants/components'
import { PrimaryButton } from 'Components/button'
import { MenuPrints, MultiUploadForm } from 'Components/form'
import GrnListTable from './components/GrnListTable'
import GrnDetail from './detail'
import { getPaymentDetailAPI, updatePaymentAPI } from 'Store/grn-payment/api'
import { PaymentDetailReq, UpdatePaymentReq } from 'Store/grn-payment/types'
import {
  selectPaymentActionEvent,
  selectPaymentDetail,
  selectPaymentDetailEvent,
} from 'Store/grn-payment/selector'
import ReactToPrint from 'react-to-print'
import { PrinterOutlined } from '@ant-design/icons'
import InvoicePDF from './components/InvoicePDF'

type Props = ReduxProps & {
  visible: boolean
  onCloseDrawer: () => void
  onSuccess?: () => void
  id?: number
  viewMode?: boolean
}

const PaymentDetail: React.FunctionComponent<Props> = props => {
  const {
    onCloseDrawer,
    visible,
    currentUser,
    event,
    onSuccess = () => {},
    id = 0,
    detail,
    detailEvent,
    viewMode = false,
  } = props
  const [files, setFiles] = useState([] as string[])
  const [detailDrawer, setDetailDrawer] = useState(false)
  const [selectedID, setSelectedID] = useState(0)
  const invoicePDF = useRef(null as any)
  const invoiceLitePDF = useRef(null as any)

  const dispatch = useDispatch()

  useEffect(() => {
    if (visible) {
      onGetDetail()
      setFiles([])
    }
  }, [visible])

  useEffect(() => {
    if (detail?.slips) {
      setFiles(detail?.slips || [])
    }
  }, [detail])

  const onGetDetail = (): void => {
    const req: PaymentDetailReq = {
      id,
    }
    dispatch(getPaymentDetailAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  const onUpdate = (): void => {
    const req: UpdatePaymentReq = {
      id: detail?.id || 0,
      slips: files,
    }
    dispatch(updatePaymentAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  const clearEvent = (): void => {
    dispatch(clearEventAction())
  }

  const onEventSuccess = (): void => {
    clearEvent()
    onGetDetail()
    //onSuccess()
  }

  const onOpenDetailDrawer = () => {
    setDetailDrawer(true)
  }

  const onCloseDetailDrawer = () => {
    setDetailDrawer(false)
  }

  const onClickViewDetail = (id: number) => {
    setSelectedID(id)
    onOpenDetailDrawer()
  }

  const renderInfo = (title: string, value: any, span: number = 15) => {
    return <InfoItem title={title} value={value} span={span} />
  }

  const renderPaymentInfo = () => {
    return (
      <BoxWrapper>
        <BoxTitle>รายละเอียดการชำระหนี้</BoxTitle>
        <Row gutter={8}>
          <Col md={12} xs={24}>
            {renderInfo('เลขที่ใบชำระหนี้', detail?.payment_no)}
            {renderInfo('วันที่ชำระ', formatDateTime(detail?.payment_date || ''))}
          </Col>
          <Col md={12} xs={24}>
            {renderInfo('ยอดที่ชำระ', `${numberWithComma(detail?.total_price || 0, true)} บาท`)}
            {renderInfo('ประเภทการชำระ', `${detail?.type === 'installment' ? 'แบ่งชำระ' : 'ชำระเต็มจำนวน'}`)}
            {renderInfo('ผู้ชำระ', detail?.username)}
          </Col>
        </Row>
      </BoxWrapper>
    )
  }

  const renderPurchaseInfo = () => {
    return (
      <BoxWrapper>
        <BoxTitle>รายละเอียดผู้จำหน่าย</BoxTitle>
        <Row gutter={8}>
          <Col md={24} xs={24}>
            {renderInfo('บริษัท/Supplier', detail?.supplier?.name)}
            {renderInfo('ที่อยู่', detail?.supplier?.address)}
            {renderInfo('เบอร์โทรศัพท์', detail?.supplier?.mobile_no)}
          </Col>
        </Row>
      </BoxWrapper>
    )
  }

  const renderClientInfo = () => {
    return (
      <BoxWrapper>
        <BoxTitle>รายละเอียดร้านค้า</BoxTitle>
        {renderInfo('ชื่อ', detail?.merchant?.name)}
        {renderInfo(
          'ที่อยู่',
          formatAddress(
            detail?.merchant?.address,
            detail?.merchant?.district,
            detail?.merchant?.amphor,
            detail?.merchant?.province,
            detail?.merchant?.postcode,
          ),
        )}
        {renderInfo('เบอร์โทรศัพท์', detail?.merchant?.phone)}
      </BoxWrapper>
    )
  }

  const renderPurchaseForm = () => {
    return (
      <BoxWrapper>
        <Row gutter={8}>
          <Col md={24} xs={24}>
            <MultiUploadForm
              label="Upload ใบจ่ายเงิน"
              token={currentUser.accessToken}
              onUpdateData={files => setFiles(files)}
              files={files}
              folder="purchasing"
              listType="picture"
              name="delivery-note"
              description="ขนาดไม่เกิน 5MB ต่อไฟล์ (.png .jpg .pdf)"
              isRequired={false}
              isError={false}
              key="delivery-note"
              maxFileSize={5000}
              readOnly={viewMode}
            />
          </Col>
          {!viewMode && (
            <Col>
              <PrimaryButton btnType="Primary" text={`บันทึกข้อมูล`} onClick={onUpdate} />
            </Col>
          )}
        </Row>
      </BoxWrapper>
    )
  }

  const GrnDetailDrawer = (
    <GrnDetail
      id={selectedID}
      visible={detailDrawer}
      onCloseDrawer={onCloseDetailDrawer}
      onSuccess={onSuccess}
    />
  )

  const renderTitle = () => {
    return (
      <Row>
        <Col span={23}>
          <Alert
            message="ชำระหนี้สำเร็จ"
            description={`เลขที่ใบชำระหนี้ : ${detail?.payment_no}`}
            type="success"
            showIcon
          />
        </Col>
      </Row>
    )
  }

  return (
    <DetailDrawer
      title={renderTitle()}
      onCloseDrawer={onCloseDrawer}
      visible={visible}
      width={'90%'}
      footer={null}
      maskClosable={true}
    >
      <LoadingView isShow={detailEvent.IsLoading} />
      <EventModal event={[event]} enable={true} successCalback={onEventSuccess} errorCalback={clearEvent} />
      {GrnDetailDrawer}
      <Row justify="space-between" gutter={[16, 16]}>
        <Col md={24} xs={24}>
          {renderPaymentInfo()}
        </Col>
        <Col md={12} xs={24}>
          {renderPurchaseInfo()}
        </Col>
        <Col md={12} xs={24}>
          {renderClientInfo()}
        </Col>
        <Col span={24}>
          <BoxWrapper>
            <Row justify="space-between">
              <Col>
                {' '}
                <BoxTitle>รายการชำระหนี้</BoxTitle>
              </Col>
              <Col>
                <Space>
                  <Dropdown
                    key="more"
                    overlay={
                      <MenuPrints
                        nomalPrintRef={invoicePDF}
                        litePrintRef={invoiceLitePDF}
                        title={`ใบชำระหนี้-${detail?.payment_no}`}
                      />
                    }
                    trigger={['click', 'hover']}
                  >
                    <PrimaryButton
                      btnType="Approve"
                      text="ใบชำระหนี้"
                      icon={<PrinterOutlined />}
                      onClick={() => {}}
                      size="middle"
                    />
                  </Dropdown>
                </Space>
              </Col>
            </Row>

            <GrnListTable datas={detail?.payment_items || []} onClickDetail={onClickViewDetail} />
            <Row justify="space-between">
              <Col>
                ประเภทการชำระ :{' '}
                <Remark> {detail?.type === 'installment' ? 'แบ่งชำระ' : 'ชำระเต็มจำนวน'} </Remark>
              </Col>
              <Col>
                ยอดที่ชำระทั้งสิ้น <Price>{numberWithComma(detail?.total_price || 0, true)}</Price> บาท
              </Col>
            </Row>
          </BoxWrapper>
        </Col>
        <Col md={24} xs={24}>
          {renderPurchaseForm()}
        </Col>
        <Col span={24} ref={invoicePDF}>
          <InvoicePDF detail={detail} />
        </Col>
        <Col span={24} ref={invoiceLitePDF}>
          <InvoicePDF detail={detail} isLite={true} />
        </Col>
      </Row>
    </DetailDrawer>
  )
}

const BoxTitle = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body3};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.primary};
`

const BoxWrapper = styled.div`
  width: 100%;
  border-radius: 10px;
  padding: 15px 10px;
  //border: 1px solid ${(props: { theme: MyTheme }): string => props.theme.colors.whitebg};
  border: 1px solid #f0f0f0;
  background-color:  ${(props: { theme: MyTheme }): string => props.theme.colors.white};
`

const Price = styled.span`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.primary};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.title1};
`

const Remark = styled.span`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.primary};
`

const mapStateToProps = (state: RootState) => {
  return {
    currentUser: selectCurrentUser(state),
    event: selectPaymentActionEvent(state),
    detail: selectPaymentDetail(state),
    detailEvent: selectPaymentDetailEvent(state),
    supplierList: selectAllSupplierList(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

export default connect(mapStateToProps)(PaymentDetail)
